import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  StickyNote,
  Copy,
  Plus,
  BookOpen,
  Layers,
  Edit2,
  Save,
  Trash2,
  Trash,
  X,
} from "lucide-react";
import { articlesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import Spinner from "../components/Spinner";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import { sumLessonItemCounts } from "../utils/lessonItemCounts";
import "./StudyLessons.css";

const AllNotes = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState(null);
  const [newName, setNewName] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const { addToast } = useToasts();

  const handleCreateNote = () => {
    navigate("/content-note");
  };

  const loadLessons = async () => {
    try {
      // Get only lessons and notes
      const [lessonsRes, summaryRes] = await Promise.all([
        articlesAPI.getMyArticles("lesson", true),
        articlesAPI.getLessonLinksSummary("note"),
      ]);

      const allLessons = lessonsRes.data.articles;
      const notesSummary = summaryRes.data.summary || [];
      const notesCountByLesson = sumLessonItemCounts(notesSummary);
      const lessonIdsWithNotes = new Set(Object.keys(notesCountByLesson));

      // Get lessons that have notes
      const lessonsWithNotes = allLessons
        .filter((a) => lessonIdsWithNotes.has(String(a._id)))
        .map((lesson) => ({
          _id: lesson._id,
          title: lesson.title,
          chapter: lesson.chapter,
          author: lesson.author,
          createdAt: lesson.createdAt,
          contentType: lesson.contentType,
          notesCount: notesCountByLesson[String(lesson._id)] || 0,
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setLessons(lessonsWithNotes);
      setLoading(false);
    } catch {
      addToast("Failed to load lessons", "error-warning");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessons();
  }, []);

  const handleCopy = async (article) => {
    try {
      const response = await articlesAPI.getLessonItems(article._id, "note");
      const notes = response.data.articles || [];
      const allText = notes.map((n) => n.body).join("\n\n");
      await navigator.clipboard.writeText(allText);
      addToast(`Copied ${notes.length} notes to clipboard`, "success-info");
    } catch {
      addToast("Failed to copy notes", "error-warning");
    }
  };

  const handleDelete = async (article) => {
    setDeletingId(null);
    setDeleteStep(0);
    try {
      const response = await articlesAPI.getLessonItems(
        article._id,
        "note",
        true,
      );
      const notes = response.data.articles || [];

      await Promise.all(
        notes.map((note) => articlesAPI.deleteArticle(note._id)),
      );

      addToast(`Deleted ${notes.length} notes successfully`, "success-info");
      loadLessons();
    } catch {
      addToast("Failed to delete notes", "error-warning");
    }
  };

  const handleRename = async (article) => {
    if (!newName.trim()) {
      addToast("Name cannot be empty", "error-warning");
      return;
    }
    try {
      const response = await articlesAPI.getArticleById(article._id);
      const fullArticle = response.data.article;
      await articlesAPI.updateArticle(article._id, {
        title: fullArticle.title,
        author: fullArticle.author,
        chapter: newName.trim(),
        body: fullArticle.body,
      });

      addToast("Lesson renamed successfully", "success-info");
      setRenamingId(null);
      setNewName("");
      loadLessons();
    } catch {
      addToast("Failed to rename lesson", "error-warning");
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async (article) => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    if (deleteStep === 2) {
      await handleDelete(article);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeleteStep(0);
  };

  const buildBreadcrumbs = () => {
    return [
      { label: "MySpace", path: "/myspace" },
      { label: "All Notes by Lesson", path: null },
    ];
  };

  if (loading) {
    return (
      <StudyLayout
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}
        buttons={<NavigationButtons />}
      >
        <Spinner visible={true} />
      </StudyLayout>
    );
  }

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}
      buttons={<NavigationButtons />}
    >
      <div className="study-lessons-grid">
        {lessons.map((article, index) => (
          <div
            key={article._id}
            className="study-lesson-card"
            onClick={() => {
              if (window.innerWidth <= 1280) {
                navigate(
                  `/study/notes?folder=${encodeURIComponent(article.title)}&lessonId=${article._id}`,
                );
              } else {
                navigate(
                  `/view/lesson/${article._id}?folder=${encodeURIComponent(article.title)}&openTakeNotes=true`,
                );
              }
            }}
            title={window.innerWidth <= 1280 ? "View notes" : "Take notes"}
          >
            <div
              className="study-lesson-btn study-lesson-card-action study-lesson-card-action-lesson"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/view/lesson/${article._id}?folder=${encodeURIComponent(article.title)}`,
                );
              }}
              title="View lesson"
            >
              <BookOpen size={20} />
            </div>
            <div
              className="study-lesson-btn study-lesson-card-action study-lesson-card-action-notes"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/notes?folder=${encodeURIComponent(article.title)}&type=note&lessonId=${article._id}`,
                );
              }}
              title="View notes"
            >
              <StickyNote size={32} />
            </div>
            <div
              className="study-lesson-btn study-lesson-card-action study-lesson-card-action-flashcards"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/study/flashcards?folder=${encodeURIComponent(article.title)}&lessonId=${article._id}`,
                );
              }}
              title="View flashcards"
            >
              <Layers size={20} />
            </div>
            <div className="study-lesson-card-action-spacer"></div>
            <div>
              {renamingId === article._id ? (
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRename(article);
                    if (e.key === "Escape") {
                      setRenamingId(null);
                      setNewName("");
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="folder-name-input"
                />
              ) : (
                <div className="study-lesson-name">
                  {article.chapter || `Lesson ${index + 1}`}
                </div>
              )}
            </div>
            <div
              className="study-lesson-info"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="study-lesson-count">{article.notesCount}</div>
              <div className="study-lesson-actions">
                <button
                  className={`study-lesson-btn ${renamingId === article._id ? "save-mode" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (renamingId === article._id) {
                      handleRename(article);
                    } else {
                      setRenamingId(article._id);
                      setNewName(article.chapter || `Lesson ${index + 1}`);
                    }
                  }}
                  title={
                    renamingId === article._id
                      ? "Save changes"
                      : "Rename lesson"
                  }
                >
                  {renamingId === article._id ? (
                    <Save size={18} />
                  ) : (
                    <Edit2 size={18} />
                  )}
                </button>
                <button
                  className="study-lesson-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(article);
                  }}
                  title="Copy all notes"
                >
                  <Copy size={18} />
                </button>
                <button
                  className="study-lesson-delete-btn"
                  onClick={(e) => handleDeleteClick(e, article._id)}
                  title="Delete all notes"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>

            {deleteStep > 0 && deletingId === article._id && (
              <div
                className="delete-confirmation-in-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="delete-confirmation-content">
                  <div className="delete-text">
                    {deleteStep === 1
                      ? `Delete all notes?`
                      : "Delete? Cannot be undone!"}
                  </div>
                  <div className="delete-actions">
                    <button
                      type="button"
                      className="delete-confirm-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConfirm(article);
                      }}
                      title={deleteStep === 1 ? "Delete" : "Yes, Delete"}
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      type="button"
                      className="delete-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCancel();
                      }}
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        <div
          className="study-lesson-card study-create-card"
          onClick={handleCreateNote}
          title="Create new note"
        >
          <StickyNote size={32} className="study-create-card-icon" />
          <Plus size={48} />
        </div>
      </div>
    </StudyLayout>
  );
};

export default AllNotes;
